import { useState } from 'react'
import { router } from '@inertiajs/react'

import { Button } from '@atoms'
import { ConfirmDialog } from '@components/molecules/ConfirmDialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/atoms/Tooltip'
import type { IconTypes } from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('comms_management.blasts_index')

function ActionButton({
  label,
  icon,
  enabled,
  reason,
  onClick,
  color,
  compact,
}: {
  label: string
  icon: IconTypes
  enabled: boolean
  reason: string | null
  onClick?: () => void
  color?: 'primary' | 'danger'
  compact?: boolean
}) {
  const button = (
    <Button
      rank="secondary"
      color={color}
      size="sm"
      leadingIcon={icon}
      disabled={!enabled}
      ariaLabel={label}
      onClick={enabled ? onClick : undefined}
    >
      <span className={compact ? 'tw:hidden tw:sm:inline' : undefined}>{label}</span>
    </Button>
  )

  if (enabled || !reason) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="tw:inline-flex" tabIndex={0} aria-label={`${label}. ${reason}`}>
          {button}
        </span>
      </TooltipTrigger>
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  )
}

export type BlastAction = {
  showApprove: boolean
  showSubmit: boolean
  showCancel: boolean
  canEdit: boolean
  canDelete: boolean
  reason: string | null
  blastUrl: string
  editUrl: string
  submitUrl: string
  title: string
  onDeleted?: () => void
}

export function BlastRowActions({ action, compact }: { action: BlastAction; compact?: boolean }) {
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  return (
    <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
      {action.showCancel && (
        <ActionButton
          label={t('actions.cancel')}
          icon="x-circle"
          enabled
          reason={null}
          compact={compact}
          onClick={() => router.visit(action.blastUrl)}
        />
      )}

      {action.showApprove && (
        <ActionButton
          label={t('actions.approve')}
          icon="check-circle"
          enabled
          reason={null}
          compact={compact}
          onClick={() => router.visit(action.blastUrl)}
        />
      )}

      {action.showSubmit && (
        <ActionButton
          label={t('actions.submit')}
          icon="check"
          enabled
          reason={null}
          compact={compact}
          onClick={() => router.post(action.submitUrl, {}, { preserveScroll: true })}
        />
      )}

      <ActionButton
        label={t('actions.edit')}
        icon="pencil-01"
        enabled={action.canEdit}
        reason={action.reason}
        compact={compact}
        onClick={() => router.visit(action.editUrl)}
      />

      <ActionButton
        label={t('actions.delete')}
        icon="trash-01"
        color="danger"
        enabled={action.canDelete}
        reason={action.reason}
        compact={compact}
        onClick={() => setConfirmingDelete(true)}
      />

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title={t('delete_dialog.title')}
        body={t('delete_dialog.body', { title: action.title })}
        confirmLabel={t('actions.delete')}
        confirmIcon="trash-01"
        onConfirm={() => {
          setConfirmingDelete(false)
          router.delete(action.blastUrl, {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => action.onDeleted?.(),
          })
        }}
      />
    </div>
  )
}
